import { useTheme } from '@mui/material/styles';
import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import MentionDropdown from './MentionDropdown';
import ActionDropdown, { availableActions } from './ActionDropdown';
import ReferencesPanel from './ReferencesPanel';
import NoteHeader from './NoteHeader';
import NoteTextEditor from './NoteTextEditor';
import NoteSaveIndicator from './NoteSaveIndicator';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { analyzeFormat } from './FormatPatterns';
import { authedApiFetch } from '../../utils/Api';
import {
	getAnchorPosition,
	updateCursorPosition,
	getElements,
	adjustTextAreaHeight,
	setupResizeHandlers
} from './domUtils';

const AIME_SERVER_URL = process.env.REACT_APP_AIME_SERVER_URL || 'http://localhost:4000';

/**
 * Note Component
 *
 * A text editor that supports @mentions and #actions with dropdown suggestions.
 * The component allows users to create notes with entity references and actions,
 * maintaining a references panel that shows detailed information about mentioned entities.
 *
 * @param {string} tenantId - ID of the current tenant
 * @param {Object} note - The note object to display
 * @param {boolean} readOnly - Whether the note is readOnly
 * @param {Function} onShowPastNotes - Callback to show past notes
 * @param {Function} onNewNote - Callback to create a new note
 * @returns {JSX.Element} The NotesInterface component
 */
const Note = ({
	note,
	readOnly = false,
	tenantId,
	onShowPastNotes,
	onNewNote,
}) => {
	const theme = useTheme();
	const [noteTitle, setNoteTitle] = useState('');
	const [noteText, setNoteText] = useState('');

	// Mentions and Actions
	const [currentMentions, setCurrentMentions] = useState([]);
	const [showMentionDropdown, setShowMentionDropdown] = useState(false);
	const [showActionDropdown, setShowActionDropdown] = useState(false);
	const [foundEntities, setFoundEntities] = useState([]);
	const [foundActions, setFoundActions] = useState([]);
	const [recentlySelectedMention, setRecentlySelectedMention] = useState(false);
	const [entityReferences, setEntityReferences] = useState({});

	// Disable actions for now
	const disableActions = true;

	useEffect(() => {
		setNoteTitle(note?.title || '');
		setNoteText(note?.content || '');

		if (note?.note_references) {
			const formattedMentions = note.note_references.map(mention => ({
				id: mention.entity_id,
				type: mention.entity_type,
				name: mention.entity_name,
				...mention.entity_details
			}));
			setCurrentMentions(formattedMentions);
		}
	}, [note]);

	// Cursor and Dropdown Position
	const [cursorPosition, setCursorPosition] = useState(0);
	const [mentionAnchorPosition, setMentionAnchorPosition] = useState(null);
	const [actionAnchorPosition, setActionAnchorPosition] = useState(null);
	const [referencesCollapsed, setReferencesCollapsed] = useState(false);

	// New state for note saving and viewing
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);

	// New state for format hints
	const [formatAnalysis, setFormatAnalysis] = useState(null);
	const [formatHintPosition, setFormatHintPosition] = useState({
		left: 0,
		top: 0,
	});

	const textAreaRef = useRef(null);

	const handleNewNote = () => {
		// Reset form and prepare for a new note
		setNoteTitle('');
		setNoteText('');
		setCurrentMentions([]);
		setEntityReferences({});
		onNewNote();
	};

	/**
	 * Handle saving the current note
	 * Saves note to backend and triggers Salesforce interaction creation
	 */
	const handleSaveNote = async () => {
		if (noteText.trim() === '') return;
		
		// Show saving indicator
		setIsSaving(true);
		setSaveError(null);
		
		try {
			// Get authentication token
			const token = sessionStorage.getItem('idToken');
			if (!token) {
				throw new Error('No authentication token found');
			}
			
			// Format references with full entity details for ReferencesPanel
			const referencesWithDetails = currentMentions.map(entity => ({
				id: entity.id,
				type: entity.type,
				name: entity.name,
				details: entity // Include all entity data for display
			}));
			
			// 1. Save to alps-be synchronously
			const savedNote = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/notes`,
				method: 'POST',
				payload: {
					note: {
						title: noteTitle || 'Untitled Note',
						content: noteText
					},
					references: referencesWithDetails,
				}
			});

			try {
				// 2. Trigger Salesforce Interaction creation asynchronously via aimeserver
				// We don't await this response as it goes through the AI and may take some time
				fetch(`${AIME_SERVER_URL}/interactions?client_type=web`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`
					},
					body: JSON.stringify({
						noteId: savedNote.id,
						noteTitle: noteTitle || 'Untitled Note',
						noteContent: noteText,
						entityReferences: referencesWithDetails
					})
				});
			} catch(error) {
				console.error('Error creating Salesforce interaction (non-blocking):', error);
			}

			handleNewNote();
		} catch (error) {
			console.error('Error saving note:', error);
			setSaveError(error.message || 'Failed to save note');
		} finally {
			setIsSaving(false);
		}
	};

	/**
	 * Load mock reference data for existing mentions when component mounts
	 */
	useEffect(() => {
		// Create mock reference data for existing mentions
		if (currentMentions.length > 0) {
			const newReferences = {};
			currentMentions.forEach((entity) => {
				newReferences[entity.id] = [
					// TODO: Replace with actual reference data
					{ title: `${entity.name} Overview`, source: 'CRM', url: '#' },
					{
						title: `Latest ${entity.type === 'deal' ? 'Deal Memo' : 'Analysis'}`,
						source: entity.type === 'deal' ? 'Pitch Deck' : 'Website',
						url: '#',
					},
					{
						title: `${entity.name} ${entity.type === 'deal' ? 'Deal Team' : 'Profile'}`,
						source: entity.type === 'deal' ? 'PitchBook' : 'CRM',
						url: '#',
					},
				];
			});
			setEntityReferences(newReferences);
		}
	}, [currentMentions]);

	/**
	 * Handle text changes and detect @ symbol and # hashtags
	 * This is the main handler that processes text input to detect mentions and actions
	 *
	 * @param {Object} e - Change event from textarea
	 */
	const handleTextChange = (e) => {
		const newValue = e.target.value;
		const cursorPos = e.target.selectionStart;

		setNoteText(newValue);
		setCursorPosition(cursorPos);

		// Extract text before cursor for analysis
		const textBeforeCursor = newValue.substring(0, cursorPos);

		// Handle @ mentions
		const atMatch = textBeforeCursor.match(/@([a-zA-Z0-9 ]*)$/);
		if (atMatch) {
			const searchTerm = atMatch[1];
			
			// Only search if we have at least 2 characters after @
			if (searchTerm.length >= 2) {
				const triggerIndex = cursorPos - searchTerm.length - 1; // position of the @ symbol

				// Set anchor position for dropdown
				setMentionAnchorPosition(getAnchorPosition(triggerIndex, textAreaRef, newValue));

				// Search for entities matching the term
				handleMentionSearch(searchTerm);
				setShowMentionDropdown(true);
				setShowActionDropdown(false);
				setFormatAnalysis(null); // Clear any format hints when showing mentions
			} else {
				setShowMentionDropdown(false);
			}
		} else {
			setShowMentionDropdown(false);
		}

		// Handle # actions and AMFI tags
		const hashMatch = !disableActions && textBeforeCursor.match(/#([a-zA-Z0-9]*)$/);
		if (hashMatch) {
			const actionTerm = hashMatch[1];
			const triggerIndex = cursorPos - actionTerm.length - 1; // position of the # symbol

			// Set anchor position for dropdown
			setActionAnchorPosition(getAnchorPosition(triggerIndex, textAreaRef, newValue));

			// Search for actions matching the term
			handleActionSearch(actionTerm);
			setShowActionDropdown(true);
			setShowMentionDropdown(false);
			setFormatAnalysis(null); // Clear any format hints when showing action dropdown
		} else {
			setShowActionDropdown(false);

			// If we're not showing a dropdown, check for format hints
			// This happens after an action has been selected
			const analysis = analyzeFormat(newValue, cursorPos);
			if (!disableActions && analysis) {
				setFormatAnalysis(analysis);

				// Set format hint position at current cursor position
				setFormatHintPosition(getAnchorPosition(cursorPos, textAreaRef, newValue));
			} else {
				setFormatAnalysis(null);
			}
		}

		// Reset the recently selected flag when user starts typing
		if (recentlySelectedMention) {
			setRecentlySelectedMention(false);
		}
	};

	/**
	 * Handle @mention search with research fallback
	 * Searches for entities matching the search term and updates dropdown
	 * If no matches are found, adds a "Research" action
	 *
	 * @param {string} searchTerm - Text after @ character to search for
	 */
	const handleMentionSearch = async (searchTerm) => {
		// Don't search if search term is empty (this check is now also in handleTextChange)
		if (searchTerm.length < 2) {
			setFoundEntities([]);
			setShowMentionDropdown(false);
			return;
		}

		try {
			// Get the Cognito ID token from session storage
			const token = sessionStorage.getItem('idToken');
			if (!token) {
				throw new Error('No authentication token found. Please log in.');
			}

			const response = await fetch(`${AIME_SERVER_URL}/mentions?client_type=web`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
				body: JSON.stringify({ text: searchTerm }),
			});

			if (!response.ok) {
				throw new Error(`AIME server error: ${response.status}`);
			}

			const data = await response.json();

			// Flatten the response into a single array, preserving type
			let combined = [];
			if (data.contacts && Array.isArray(data.contacts)) {
				combined = combined.concat(data.contacts.map(e => ({ ...e, type: 'contact' })));
			}

			if (data.accounts && Array.isArray(data.accounts)) {
				combined = combined.concat(data.accounts.map(e => ({ ...e, type: 'account' })));
			}

			if (data.deals && Array.isArray(data.deals)) {
				combined = combined.concat(data.deals.map(e => ({ ...e, type: 'deal' })));
			}

			// If no matches, add a research action
			if (combined.length === 0) {
				if (disableActions) {
					const noResultsItem = {
						id: `no-results-${Date.now()}`,
						name: 'No results',
						type: 'message',
						action: 'research',  // Keep the action type for display purposes
						query: searchTerm,  // Pass the search term to display in the message
					};
					setFoundEntities([noResultsItem]);
				} else {
					const researchAction = {
						id: `research-${Date.now()}`,
						name: `Research "${searchTerm}"`,
						type: 'action',
						action: 'research',
						searchTerm,
					};
					setFoundEntities([researchAction]);
				}
				setShowMentionDropdown(true);
			} else {
				// Limit to top 5 matches
				setFoundEntities(combined.slice(0, 5));
				setShowMentionDropdown(true);
			}
		} catch (error) {
			console.error('Mention search error:', error);
			setFoundEntities([]);
			setShowMentionDropdown(false);
		}

		setShowActionDropdown(false);
	};

	/**
	 * Handle #action search
	 * Filters available actions based on the search term
	 *
	 * @param {string} actionTerm - Text after # character to search for
	 */
	const handleActionSearch = (actionTerm) => {
		// Updated to include AMFI tags
		const allActions = [
			...availableActions,
			{
				id: 'attribute',
				name: 'attribute',
				description: 'Add an attribute tag',
				icon: (_isDark) => null,
			},
			{
				id: 'metric',
				name: 'metric',
				description: 'Add a metric tag',
				icon: (_isDark) => null,
			},
			{
				id: 'fact',
				name: 'fact',
				description: 'Add a fact tag',
				icon: (_isDark) => null,
			},
			{
				id: 'insight',
				name: 'insight',
				description: 'Add an insight tag',
				icon: (_isDark) => null,
			},
		];

		// Filter based on search term
		if (actionTerm === '') {
			// Show all actions when just # is typed
			setFoundActions(allActions);
		} else {
			// Filter by term
			const filtered = allActions.filter((action) =>
				action.name.toLowerCase().includes(actionTerm.toLowerCase())
			);
			setFoundActions(filtered);
		}
	};

	/**
	 * Handle selecting a mention from dropdown
	 * Inserts the mention into the text and updates references
	 *
	 * @param {Object} entity - Selected entity from dropdown
	 */
	const selectMention = (entity) => {
		// Skip any action if selecting the "No results" message
		if (disableActions && entity.type === 'message') {
			setShowMentionDropdown(false);
			return;
		}
		
		// Capture current text and position
		const textBeforeCursor = noteText.substring(0, cursorPosition);
		const textAfterCursor = noteText.substring(cursorPosition);

		// Find the @ symbol index
		const atSignIndex = textBeforeCursor.lastIndexOf('@');

		// Set flag to prevent dropdown from re-opening immediately
		setRecentlySelectedMention(true);

		// Reset the flag after a short delay to allow typing to continue
		setTimeout(() => {
			setRecentlySelectedMention(false);
		}, 500);

		// Check if this is a research action (special handling)
		if (entity.type === 'action' && entity.action === 'research') {
			// Add both the mention and the research action, with a clear marker at the end
			const mentionText = `@${entity.searchTerm}\u200B `;
			const actionText = '#research\u200B ';

			// Add a special no-width space marker followed by a normal space to signify end of research action
			const completionMarker = '\u200B\u200B ';

			// Replace the @mention with the entity and add #research with a space
			const newText =
				textBeforeCursor.substring(0, atSignIndex) +
				mentionText +
				actionText +
				completionMarker +
				textAfterCursor;

			setNoteText(newText);
			setShowMentionDropdown(false);

			// Create a placeholder entity for this research term
			const placeholderEntity = {
				id: entity.id,
				name: entity.searchTerm,
				type: 'research',
				status: 'pending',
			};

			// Add the placeholder entity to references
			if (
				!currentMentions.some((mention) => mention.id === placeholderEntity.id)
			) {
				setCurrentMentions([...currentMentions, placeholderEntity]);

				setEntityReferences((prev) => ({
					...prev,
					[placeholderEntity.id]: [
						{
							title: `Searching for "${entity.searchTerm}"`,
							source: 'AIMe Research',
							url: '#',
							status: 'pending',
						},
					],
				}));
			}

			// Calculate new cursor position
			const newCursorPos =
				atSignIndex +
				mentionText.length +
				actionText.length +
				completionMarker.length;

			// Focus and set cursor after both insertions
			updateCursorPosition(textAreaRef, newCursorPos);
			return;
		}

		// Regular entity mention
		const mentionText = `@${entity.name}\u200B `;

		// Create new text with the mention replaced
		const newText =
			textBeforeCursor.substring(0, atSignIndex) +
			mentionText +
			textAfterCursor;

		// Set text and close dropdown
		setNoteText(newText);
		setShowMentionDropdown(false);

		// Add to mentions if not already in the list
		if (!currentMentions.some((mention) => mention.id === entity.id)) {
			setCurrentMentions([...currentMentions, entity]);

			// Create reference data for the new entity
			setEntityReferences((prev) => ({
				...prev,
				[entity.id]: [
					{ title: `${entity.name} Profile`, source: 'CRM', url: '#' },
					{
						title: `${entity.name} ${entity.type === 'company' ? 'Team' : 'Profile'}`,
						source: entity.type === 'company' ? 'PitchBook' : 'LinkedIn',
						url: '#',
					},
				],
			}));
		}

		// Focus and set cursor after inserted mention
		const newCursorPos = atSignIndex + mentionText.length;
		updateCursorPosition(textAreaRef, newCursorPos);
	};

	/**
	 * Handle selecting an action from dropdown
	 * Inserts the action into the text
	 *
	 * @param {Object} action - Selected action from dropdown
	 */
	const selectAction = (action) => {
		// Get the text before and after cursor
		const textBeforeCursor = noteText.substring(0, cursorPosition);
		const textAfterCursor = noteText.substring(cursorPosition);

		// Find the # symbol index
		const hashtagIndex = textBeforeCursor.lastIndexOf('#');

		// Format without a colon (colons start segments, not end them)
		let actionText = `#${action.id}\u200B `;

		// Replace the #action with the formatted text
		const newText =
			textBeforeCursor.substring(0, hashtagIndex) +
			actionText +
			textAfterCursor;

		setNoteText(newText);
		setShowActionDropdown(false);

		// Calculate and set cursor position immediately
		const newCursorPos = hashtagIndex + actionText.length;
		updateCursorPosition(textAreaRef, newCursorPos);
		
		// Set flag to prevent dropdown from re-opening immediately
		setRecentlySelectedMention(true);

		// Reset the flag after a short delay to allow typing to continue
		setTimeout(() => {
			setRecentlySelectedMention(false);

			// Analyze format and set hint after a brief delay to ensure cursor is positioned
			setTimeout(() => {
				const analysis = analyzeFormat(newText, newCursorPos);
				if (analysis && !disableActions) {
					setFormatAnalysis(analysis);
					setFormatHintPosition(getAnchorPosition(newCursorPos, textAreaRef, newText));
				}
			}, 10);
		}, 10);
	};

	/**
	 * Handle removing a mention
	 * Removes an entity from the mentions list and its references
	 *
	 * @param {string} entityId - ID of the entity to remove
	 */
	const removeMention = (entityId) => {
		setCurrentMentions(currentMentions.filter((m) => m.id !== entityId));

		// Also remove from entityReferences to clean up
		setEntityReferences((prev) => {
			const newRefs = { ...prev };
			delete newRefs[entityId];
			return newRefs;
		});

		// Hide Mention Dropdown
		setShowMentionDropdown(false);
	};

	/**
	 * Handle resize initiation for the resize handle
	 * Prevents default behavior if references panel is not collapsed
	 * to allow custom resize handling
	 *
	 * @param {MouseEvent} e - The mouse event from the resize handle
	 */
	const handleResize = (e) => {
		// Only handle resize if references are not collapsed
		if (!referencesCollapsed) {
			e.preventDefault();
		}
	};

	/**
	 * Initialize component heights on mount
	 * Sets up initial heights for the references panel and text area
	 * and stores the original height for later use when collapsing/expanding
	 */
	useEffect(() => {
		const { noteContentArea, noteInputCard, referencesCard } = getElements();
		
		if (!noteContentArea || !noteInputCard || !referencesCard) return;
		
		// Get the current height from CSS
		const currentHeight = referencesCard.offsetHeight;
		
		// Store the initial height for references panel
		if (currentHeight > 50) {
			referencesCard.dataset.originalHeight = `${currentHeight}px`;
		}
		
		// Adjust text area height based on references panel height
		adjustTextAreaHeight(noteContentArea, noteInputCard, currentHeight);
	}, []);

	/**
	 * Handle resize between textarea and references panel
	 * Sets up event listeners for the resize handle to allow users to
	 * adjust the relative sizes of the text area and references panel
	 *
	 * @dependency {boolean} referencesCollapsed - Whether the references panel is collapsed
	 */
	useEffect(() => {
		if (referencesCollapsed) return;

		const { noteInputCard, referencesCard, resizeHandle } = getElements();

		if (!resizeHandle || !noteInputCard || !referencesCard) return;

		setupResizeHandlers(resizeHandle, noteInputCard, referencesCard);
	}, [referencesCollapsed]);

	/**
	 * Adjust layout when references panel is collapsed/expanded
	 * Updates the UI when the references panel state changes between
	 * collapsed and expanded states, preserving heights and adjusting
	 * the text area to fill the available space
	 *
	 * @dependency {boolean} referencesCollapsed - Whether the references panel is collapsed
	 */
	useEffect(() => {
		const { noteContentArea, noteInputCard, referencesCard } = getElements();

		if (!noteContentArea || !noteInputCard || !referencesCard) return;

		if (referencesCollapsed) {
			// Store current height before collapsing if not already stored
			if (!referencesCard.dataset.originalHeight && referencesCard.offsetHeight > 50) {
				referencesCard.dataset.originalHeight = `${referencesCard.offsetHeight}px`;
			}

			// Collapse panel
			referencesCard.classList.add('collapsed');
			referencesCard.classList.remove('expanded');

			// Make text area fill available space (50px for collapsed panel)
			adjustTextAreaHeight(noteContentArea, noteInputCard, 50);
		} else {
			// Expand panel
			referencesCard.classList.remove('collapsed');
			referencesCard.classList.add('expanded');

			// Restore original height if available
			if (referencesCard.dataset.originalHeight) {
				const originalHeight = referencesCard.dataset.originalHeight;
				referencesCard.style.height = originalHeight;

				// Adjust text area height to fill remaining space
				const refHeight = parseInt(originalHeight, 10);
				adjustTextAreaHeight(noteContentArea, noteInputCard, refHeight);
			}
		}
	}, [referencesCollapsed]);

	// Styles objects
	const styles = {
		textArea: {
			border: `1px solid ${theme.palette.divider}`,
			backgroundColor: theme.palette.background.paper,
			color: theme.palette.text.primary,
		},
		formatHint: {
			backgroundColor:
				theme.palette.mode === 'dark'
					? theme.palette.background.paper
					: '#f0f0f0',
			border: `1px solid ${theme.palette.divider}`,
			boxShadow: theme.shadows[3],
			color:
				theme.palette.mode === 'dark' ? '#e2e8f0' : '#2d3748',
		},
	};
	
	return (
		<div
			className={`aime-panel notes-mode${theme.palette.mode === 'dark' ? ' dark' : ''}`}
		>
			{/* Error indicator for note saving */}
			<NoteSaveIndicator 
				error={saveError} 
				onErrorClear={() => setSaveError(null)} 
			/>
			
			{/* Note header with title and action buttons */}
			<NoteHeader
				title={noteTitle}
				readOnly={readOnly}
				isSaving={isSaving}
				onTitleChange={setNoteTitle}
				onNewNote={handleNewNote}
				onSaveNote={handleSaveNote}
				onShowPastNotes={onShowPastNotes}
			/>

			<div className='note-content-area'>
				<NoteTextEditor
					content={noteText}
					readOnly={readOnly}
					onChange={handleTextChange}
					onCursorPositionChange={setCursorPosition}
					textAreaRef={textAreaRef}
				/>

				{/* Mention dropdown using MUI Popper */}
				<Popper
					open={
						showMentionDropdown &&
						foundEntities.length > 0 &&
						!recentlySelectedMention &&
						!isSaving
					}
					anchorEl={mentionAnchorPosition}
					placement='bottom-start'
					className={`dropdown-container ${theme.palette.mode === 'dark' ? 'dark' : ''}`}
					modifiers={[
						{
							name: 'offset',
							options: {
								offset: [0, 5],
							},
						},
					]}
				>
					<div
						className={`mention-dropdown-wrapper ${theme.palette.mode === 'dark' ? 'dark' : ''}`}
					>
						<MentionDropdown
							options={foundEntities}
							onSelect={selectMention}
							onClose={() => setShowMentionDropdown(false)}
							disableActions={disableActions}
						/>
					</div>
				</Popper>

				{/* Action dropdown using MUI Popper */}
				<Popper
					open={
						!disableActions &&
						showActionDropdown &&
						foundActions.length > 0 &&
						!recentlySelectedMention &&
						!isSaving
					}
					anchorEl={actionAnchorPosition}
					placement='bottom-start'
					className={`dropdown-container ${theme.palette.mode === 'dark' ? 'dark' : ''}`}
					modifiers={[
						{
							name: 'offset',
							options: {
								offset: [0, 5],
							},
						},
					]}
				>
					<div
						className={`mention-dropdown-wrapper ${theme.palette.mode === 'dark' ? 'dark' : ''}`}
					>
						<ActionDropdown
							options={foundActions}
							onSelect={selectAction}
							onClose={() => setShowActionDropdown(false)}
						/>
					</div>
				</Popper>

				{/* Format hint using Popper for proper positioning */}
				<Popper
					open={formatAnalysis !== null && !isSaving}
					anchorEl={formatHintPosition}
					placement='top-end'
					className={`dropdown-container ${theme.palette.mode === 'dark' ? 'dark' : ''}`}
					modifiers={[
						{
							name: 'offset',
							options: {
								offset: [20, -30], // Shifted to the right to avoid hiding typing
							},
						},
						{
							name: 'preventOverflow',
							options: {
								padding: 10,
								boundary: document.body,
							},
						},
					]}
				>
					{formatAnalysis && (
						<div
							className="format-hint"
							style={styles.formatHint}
						>
							Next, type{' '}
							<span className="format-hint-code">
								{formatAnalysis.hint}
							</span>
						</div>
					)}
				</Popper>

				{/* Custom resize handle */}
				{!readOnly && 
					<div className="resize-handle" onMouseDown={handleResize}></div>
				}

				{/* References section */}
				<ReferencesPanel
					currentMentions={currentMentions}
					entityReferences={entityReferences}
					removeMention={removeMention}
					isCollapsed={referencesCollapsed}
					toggleCollapsed={() => setReferencesCollapsed(!referencesCollapsed)}
					readOnly={readOnly}
				/>
			</div>

			{readOnly && (
				<Box>
					{/* Salesforce Interaction Link */}
					{note?.sf_interaction_url && (
						<Typography variant="subtitle2" color="textSecondary" display="block" mb={0.5}>
							<a href={note.sf_interaction_url} target="_blank" rel="noopener noreferrer">
								View in CRM
							</a>
						</Typography>
					)}
					{/* Created Date */}
					{note?.created_at && (
						<Typography variant="subtitle2" color="textSecondary" display="block" mb={1}>
							Created: {new Date(note.created_at).toLocaleString()}
						</Typography>
					)}
				</Box>
			)}
		</div>
	);
};

Note.propTypes = {
	note: PropTypes.shape({
		title: PropTypes.string,
		content: PropTypes.string,
		note_references: PropTypes.arrayOf(
			PropTypes.shape({
				entity_id: PropTypes.string.isRequired,
				entity_type: PropTypes.string.isRequired,
				entity_name: PropTypes.string.isRequired,
				entity_details: PropTypes.object
			})
		),
		sf_interaction_url: PropTypes.string,
		created_at: PropTypes.string
	}),
	readOnly: PropTypes.bool,
	tenantId: PropTypes.string,
	onShowPastNotes: PropTypes.func,
	onNewNote: PropTypes.func
};

export default Note;
